import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { matchAPI, fieldAPI, userAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  MapPin,
  Loader2,
  ChevronLeft,
  Star,
  UserPlus,
  Trophy
} from 'lucide-react';
import { formatCurrency, cn, generateInitials } from '../lib/utils';

// Sample fields for selection
const sampleFields = [
  { id: '1', name: 'Stade El Menzah', location: 'El Menzah, Tunis', price_per_hour: 80 },
  { id: '2', name: 'City Soccer Ariana', location: 'Ariana, Tunis', price_per_hour: 60 },
  { id: '3', name: 'La Marsa Football Center', location: 'La Marsa, Tunis', price_per_hour: 100 },
  { id: '4', name: 'Hammam-Lif Arena', location: 'Hammam-Lif, Tunis', price_per_hour: 50 },
];

export default function CreateMatch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [selectedFavorites, setSelectedFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  const [formData, setFormData] = useState({
    field_id: '',
    date: null,
    time: '',
    duration_hours: 2,
    max_players: 10,
    description: '',
  });

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await fieldAPI.getAll();
        const apiFields = response.data || [];
        setFields(apiFields.length > 0 ? apiFields : sampleFields);
      } catch (error) {
        setFields(sampleFields);
      }
    };
    fetchFields();
  }, []);

  // Fetch favorite players
  useEffect(() => {
    const fetchFavorites = async () => {
      setLoadingFavorites(true);
      try {
        const response = await userAPI.getFavorites();
        setFavorites(response.data || []);
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoadingFavorites(false);
      }
    };
    fetchFavorites();
  }, []);

  // Fetch available slots when field and date change
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!formData.field_id || !formData.date) {
        setAvailableSlots([]);
        return;
      }
      
      setLoadingSlots(true);
      try {
        const dateStr = format(formData.date, 'yyyy-MM-dd');
        const response = await fieldAPI.getAvailability(formData.field_id, dateStr);
        const slots = response.data.slots || [];
        // Filter only available slots
        const available = slots.filter(s => s.status === 'available');
        setAvailableSlots(available);
        
        // Reset time if previously selected time is no longer available
        if (formData.time && !available.find(s => s.start_time === formData.time)) {
          setFormData(prev => ({ ...prev, time: '' }));
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    
    fetchAvailability();
  }, [formData.field_id, formData.date]);

  const selectedField = fields.find(f => f.id === formData.field_id);
  const pricePerPlayer = selectedField 
    ? (selectedField.price_per_hour * formData.duration_hours) / formData.max_players 
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.field_id) {
      toast.error('Bitte wähle ein Feld aus');
      return;
    }
    
    if (!formData.date) {
      toast.error('Bitte wähle ein Datum aus');
      return;
    }
    
    if (!formData.time) {
      toast.error('Bitte wähle eine verfügbare Uhrzeit aus');
      return;
    }
    
    setLoading(true);
    
    try {
      const matchData = {
        field_id: formData.field_id,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        duration_hours: formData.duration_hours,
        max_players: formData.max_players,
        description: formData.description || null,
      };
      
      const response = await matchAPI.create(matchData);
      const matchId = response.data.id;
      
      // Send invitations to selected favorites
      if (selectedFavorites.length > 0) {
        try {
          await matchAPI.invitePlayers(matchId, selectedFavorites);
          toast.success(`Spiel erstellt! ${selectedFavorites.length} Einladung(en) gesendet.`);
        } catch (inviteError) {
          toast.success('Spiel erstellt! Einladungen konnten nicht gesendet werden.');
        }
      } else {
        toast.success('Spiel erfolgreich erstellt!');
      }
      
      navigate(`/matches/${matchId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Spiels');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="create-match-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-display font-bold text-2xl text-tunis-blue">
              Neues Spiel erstellen
            </h1>
            <p className="text-muted-foreground">Lade Spieler zu deinem Spiel ein</p>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Field Selection */}
              <div className="space-y-2">
                <Label>Spielfeld</Label>
                <Select
                  value={formData.field_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, field_id: value }))}
                >
                  <SelectTrigger className="h-12" data-testid="select-field">
                    <SelectValue placeholder="Wähle ein Feld aus" />
                  </SelectTrigger>
                  <SelectContent>
                    {fields.map((field) => (
                      <SelectItem key={field.id} value={field.id}>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-tunis-teal" />
                          <span>{field.name}</span>
                          <span className="text-muted-foreground">
                            - {formatCurrency(field.price_per_hour)}/Std.
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedField && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selectedField.location}
                  </p>
                )}
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label>Datum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-12 justify-start text-left font-normal",
                        !formData.date && "text-muted-foreground"
                      )}
                      data-testid="select-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(formData.date, 'PPP', { locale: de }) : 'Datum auswählen'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time and Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Uhrzeit</Label>
                  <Select
                    value={formData.time}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                    disabled={!formData.field_id || !formData.date || loadingSlots}
                  >
                    <SelectTrigger className="h-12" data-testid="select-time">
                      <SelectValue placeholder={
                        loadingSlots ? "Lädt..." : 
                        !formData.field_id ? "Erst Feld wählen" :
                        !formData.date ? "Erst Datum wählen" :
                        availableSlots.length === 0 ? "Keine verfügbar" :
                        "Zeit wählen"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Keine verfügbaren Zeiten
                        </div>
                      ) : (
                        availableSlots.map((slot) => (
                          <SelectItem key={slot.start_time} value={slot.start_time}>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-tunis-teal" />
                              {slot.start_time} - {slot.end_time}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {formData.field_id && formData.date && availableSlots.length === 0 && !loadingSlots && (
                    <p className="text-xs text-amber-600">
                      Alle Zeiten sind blockiert oder gebucht
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Dauer</Label>
                  <Select
                    value={formData.duration_hours.toString()}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, duration_hours: parseFloat(value) }))}
                  >
                    <SelectTrigger className="h-12" data-testid="select-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Stunde (60 Min)</SelectItem>
                      <SelectItem value="1.5">1,5 Stunden (90 Min)</SelectItem>
                      <SelectItem value="2">2 Stunden (120 Min)</SelectItem>
                      <SelectItem value="2.5">2,5 Stunden (150 Min)</SelectItem>
                      <SelectItem value="3">3 Stunden (180 Min)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Max Players */}
              <div className="space-y-2">
                <Label>Maximale Spieleranzahl</Label>
                <Select
                  value={formData.max_players.toString()}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, max_players: parseInt(value) }))}
                >
                  <SelectTrigger className="h-12" data-testid="select-players">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 Spieler (3v3)</SelectItem>
                    <SelectItem value="8">8 Spieler (4v4)</SelectItem>
                    <SelectItem value="10">10 Spieler (5v5)</SelectItem>
                    <SelectItem value="12">12 Spieler (6v6)</SelectItem>
                    <SelectItem value="14">14 Spieler (7v7)</SelectItem>
                    <SelectItem value="22">22 Spieler (11v11)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Beschreibung (optional)</Label>
                <Textarea
                  placeholder="Zusätzliche Infos zum Spiel..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="input-description"
                />
              </div>

              {/* Invite Favorite Players */}
              {favorites.length > 0 && (
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Stammspieler einladen
                  </Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {favorites.map((player) => {
                      const isSelected = selectedFavorites.includes(player.id);
                      return (
                        <div
                          key={player.id}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors",
                            isSelected
                              ? "bg-tunis-teal/10 border border-tunis-teal"
                              : "hover:bg-slate-50 border border-transparent"
                          )}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedFavorites(selectedFavorites.filter(id => id !== player.id));
                            } else {
                              setSelectedFavorites([...selectedFavorites, player.id]);
                            }
                          }}
                        >
                          <div className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center",
                            isSelected ? "bg-tunis-teal border-tunis-teal" : "border-slate-300"
                          )}>
                            {isSelected && <span className="text-white text-xs">✓</span>}
                          </div>
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={player.avatar_url} />
                            <AvatarFallback className="bg-gradient-to-br from-tunis-blue to-tunis-teal text-white text-xs">
                              {generateInitials(player.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{player.name}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Trophy className="w-3 h-3" />
                              {player.elo_points || 1000} ELO
                            </p>
                          </div>
                          {isSelected && (
                            <Badge className="bg-tunis-teal text-white text-xs">
                              <UserPlus className="w-3 h-3 mr-1" />
                              Einladen
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {selectedFavorites.length > 0 && (
                    <p className="text-sm text-tunis-teal">
                      {selectedFavorites.length} Spieler werden eingeladen (Plätze werden reserviert)
                    </p>
                  )}
                </div>
              )}

              {favorites.length === 0 && !loadingFavorites && (
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Star className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Füge Stammspieler hinzu, um sie direkt einzuladen
                  </p>
                  <Button
                    type="button"
                    variant="link"
                    className="text-tunis-teal"
                    onClick={() => navigate('/favorites')}
                  >
                    Stammspieler verwalten →
                  </Button>
                </div>
              )}

              {/* Price Summary */}
              {selectedField && (
                <div className="p-4 bg-tunis-teal/10 rounded-xl space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Feldkosten</span>
                    <span>{formatCurrency(selectedField.price_per_hour * formData.duration_hours)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Aufgeteilt auf</span>
                    <span>{formData.max_players} Spieler</span>
                  </div>
                  <div className="flex justify-between font-bold text-tunis-blue pt-2 border-t border-tunis-teal/20">
                    <span>Preis pro Spieler</span>
                    <span className="text-tunis-teal">{formatCurrency(pricePerPlayer)}</span>
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full btn-primary h-12"
                disabled={loading}
                data-testid="btn-create-match"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Spiel erstellen'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
